import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "./services/api";
import { useWebSocket } from "./hooks/useWebSocket";
import { ConsoleContext, TABS } from "./lib/console";
import { useHashRoute, useStoredState } from "./lib/hooks";
import { isIPv4, plural, toDate } from "./lib/format";
import { Button, Spinner, ToastProvider, cx, useToast } from "./components/ui";
import { Banner, CommandPalette, Logo, Sidebar, TopBar } from "./components/ConsoleShell";
import { WarRoom } from "./components/GeoMap";
import LandingPage from "./components/LandingPage";
import AttackerProfile from "./components/AttackerProfile";
import DetectionDrawer from "./components/DetectionDrawer";
import Overview from "./pages/Overview";
import Detections from "./pages/Detections";
import Investigations from "./pages/Investigations";
import Response from "./pages/Response";
import Analytics from "./pages/Analytics";
import Models from "./pages/Models";
import Risk from "./pages/Risk";
import Reports from "./pages/Reports";
import Assistant from "./pages/Assistant";
import Settings from "./pages/Settings";
import Basic from "./pages/Basic";

// ── Information architecture ────────────────────────────────────
export const NAV = [
  {
    label: "Monitor",
    items: [
      { id: "overview", label: "Overview", icon: "grid" },
      { id: "detections", label: "Detections", icon: "radar" },
      { id: "investigations", label: "Investigations", icon: "search" },
    ],
  },
  {
    label: "Protect",
    items: [
      { id: "response", label: "Response", icon: "shield" },
      { id: "risk", label: "Risk & compliance", icon: "gauge" },
    ],
  },
  {
    label: "Intelligence",
    items: [
      { id: "analytics", label: "Analytics", icon: "barChart" },
      { id: "models", label: "Models", icon: "cpu" },
      { id: "assistant", label: "AI analyst", icon: "message" },
    ],
  },
  { label: "Workspace", items: [{ id: "reports", label: "Reports", icon: "fileText" }, { id: "settings", label: "Settings", icon: "settings" }] },
];

// Basic mode: a plain-language view for presentations and non-specialists.
const BASIC_NAV = [
  {
    label: "Simple view",
    items: [
      { id: "home", label: "Home", icon: "grid" },
      { id: "assistant", label: "Ask the AI", icon: "message" },
      { id: "reports", label: "Reports", icon: "fileText" },
    ],
  },
];

const PAGES = Object.fromEntries([...BASIC_NAV, ...NAV].flatMap((g) => g.items).map((p) => [p.id, p]));

// Real attack surfaces served from public/ (not simulated); they open in a new tab.
const DEMO_LINKS = [
  { href: "/target.html", label: "Bank demo (target)", icon: "target", hint: "Khalifa University Islamic Bank sign-in page. Real attempts against it are detected live." },
  { href: "/admin.html", label: "Honeypot portal", icon: "eye", hint: "Decoy admin page. Opening it blocks your IP until it is released on the Response page." },
];

// ── Situation assessment ────────────────────────────────────────
// Based on the last 10 minutes of activity (the all-time total only grows,
// so basing the level on it would leave it stuck on "critical").
function assessLevel(threats, alerts, now) {
  const ms = (t) => toDate(t)?.getTime() ?? 0;
  const recent = (threats || []).filter((t) => t.timestamp && t.is_threat !== false && ms(t.timestamp) >= now - 10 * 60 * 1000);
  const contained = recent.filter((t) => t.contained).length;
  const open = (alerts || []).filter((a) => !a.acknowledged && a.timestamp && ms(a.timestamp) >= now - 30 * 60 * 1000);
  const openCritical = open.filter((a) => a.severity === "CRITICAL").length;
  const openHigh = open.filter((a) => a.severity === "HIGH").length;
  const base = { recent: recent.length, contained, open: open.length, openCritical, openList: open };

  if (openCritical >= 3)
    return {
      ...base,
      tone: "critical",
      label: "Critical",
      icon: "shieldAlert",
      title: "Analyst action needed",
      msg: `${plural(openCritical, "critical alert")} waiting for review. ${contained} other ${contained === 1 ? "incident was" : "incidents were"} contained automatically.`,
    };
  if (openCritical > 0 || openHigh >= 3 || open.length >= 8)
    return {
      ...base,
      tone: "medium",
      label: "Elevated",
      icon: "bell",
      title: "Under watch",
      msg: `${plural(open.length, "alert")} need review. ${contained} ${contained === 1 ? "incident was" : "incidents were"} contained automatically in the last 10 minutes.`,
    };
  return {
    ...base,
    tone: "ok",
    label: "Normal",
    icon: "shield",
    title: recent.length ? "Threats are being contained" : "All quiet",
    msg: recent.length
      ? `${plural(recent.length, "detection")} in the last 10 minutes, ${contained} blocked automatically. Nothing needs an analyst right now.`
      : "No significant threat activity in the last 10 minutes.",
  };
}

// ── Root ────────────────────────────────────────────────────────
export default function App() {
  return (
    <ToastProvider>
      <Root />
    </ToastProvider>
  );
}

function Root() {
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem("cybershield_token") || "";
    } catch {
      return "";
    }
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [theme, setTheme] = useState(() => document.documentElement.getAttribute("data-theme") || "dark");

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("cybershield_theme", theme);
    } catch {
      /* storage unavailable */
    }
  }, [theme]);

  useEffect(() => {
    if (!token) {
      setAuthChecked(true);
      return;
    }
    api
      .get("/api/auth/verify", { params: { token } })
      .then((r) => {
        if (!r.data.valid) {
          try {
            localStorage.removeItem("cybershield_token");
          } catch {
            /* ignore */
          }
          setToken("");
        }
      })
      .catch(() => {
        // Backend unreachable: keep the token and let the console show its disconnected state
      })
      .finally(() => setAuthChecked(true));
    // eslint-disable-next-line
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/api/auth/logout", null, { params: { token } });
    } catch {
      /* ignore */
    }
    try {
      localStorage.removeItem("cybershield_token");
    } catch {
      /* ignore */
    }
    window.location.hash = "";
    setToken("");
  }, [token]);

  if (!authChecked)
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center" }}>
        <div className="row" style={{ color: "var(--text-2)", gap: 12 }}>
          <Logo size={34} />
          <Spinner size={16} />
          <span>Verifying your session…</span>
        </div>
      </div>
    );

  if (!token)
    return (
      <LandingPage
        theme={theme}
        onToggleTheme={() => setTheme((t) => (t === "dark" ? "light" : "dark"))}
        onLogin={(t) => {
          try {
            localStorage.setItem("cybershield_token", t);
          } catch {
            /* ignore */
          }
          setToken(t);
        }}
      />
    );

  return <Console theme={theme} setTheme={setTheme} onLogout={logout} />;
}

// ── Console ─────────────────────────────────────────────────────
function Console({ theme, setTheme, onLogout }) {
  const toast = useToast();
  const [mode, setModeState] = useStoredState("cybershield_mode", "basic");
  const [route, navigate] = useHashRoute(mode === "basic" ? "home" : "overview");
  const navGroups = mode === "basic" ? BASIC_NAV : NAV;
  const home = mode === "basic" ? "home" : "overview";
  const page = PAGES[route.page] ? route.page : home;

  const [threats, setThreats] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);
  const [timeline, setTimeline] = useState(null);
  const [mlMetrics, setMlMetrics] = useState(null);
  const [blockedIPs, setBlockedIPs] = useState(() => new Set());
  const [blockedUsers, setBlockedUsers] = useState(() => new Set());
  const [sim, setSim] = useState(null);
  const [score, setScore] = useState(null);
  const [predictions, setPredictions] = useState(null);
  const [everConnected, setEverConnected] = useState(false);
  const [graceOver, setGraceOver] = useState(false);

  const [profileIP, setProfileIP] = useState(null);
  const [detection, setDetection] = useState(null);
  const [warRoom, setWarRoom] = useState(false);
  const [palette, setPalette] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [rail, setRail] = useStoredState("cybershield_rail", false);
  const [huntPrefill, setHuntPrefill] = useState(null);
  const [portal, setPortal] = useState({ active: [], events: [] });

  // Live stream
  const onMessage = useCallback((msg) => {
    switch (msg.type) {
      case "initial_state":
        setThreats(msg.recent_threats || []);
        setAlerts(msg.recent_alerts || []);
        if (msg.stats) setStats(msg.stats);
        setTimeline(msg.timeline || []);
        if (msg.ml_metrics) setMlMetrics(msg.ml_metrics);
        if (msg.portal) setPortal(msg.portal);
        break;
      case "portal_presence": {
        if (msg.portal) setPortal(msg.portal);
        const r = msg.record;
        if (r?.event === "alerted")
          toast(`Alert sent to the ${r.username} account holder`, "medium", { detail: `A wrong password was entered for their account (${r.reason}).` });
        else if (r)
          toast(r.event === "signed_in" ? `${r.username} signed in to the bank portal` : `${r.username} left the bank portal`, "ok", {
            detail: r.event === "signed_in" ? "Normal activity: a registered customer." : `Normal activity: ${r.reason || "session ended"}.`,
          });
        break;
      }
      case "new_threat":
        if (msg.threat) setThreats((p) => [{ ...msg.threat, _new: Date.now() }, ...p].slice(0, 500));
        if (msg.alert) setAlerts((p) => [msg.alert, ...p].slice(0, 200));
        if (msg.stats) setStats(msg.stats);
        break;
      case "new_event":
        if (msg.stats) setStats(msg.stats);
        break;
      case "ip_blocked":
        if (msg.ip) setBlockedIPs((p) => new Set([...p, msg.ip]));
        break;
      case "ip_unblocked":
        if (msg.ip)
          setBlockedIPs((p) => {
            const n = new Set(p);
            n.delete(msg.ip);
            return n;
          });
        break;
      default:
        break;
    }
  }, [toast]);
  const { connected } = useWebSocket(onMessage);
  useEffect(() => {
    if (connected) setEverConnected(true);
  }, [connected]);
  useEffect(() => {
    const t = setTimeout(() => setGraceOver(true), 5000);
    return () => clearTimeout(t);
  }, []);

  // Polled platform state
  const [apiDown, setApiDown] = useState(false);
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      const results = await Promise.allSettled([
        api.get("/api/stats"),
        api.get("/api/ips/blocked"),
        api.get("/api/simulation/status"),
        api.get("/api/predictions"),
        api.get("/api/users/blocked"),
      ]);
      if (!alive) return;
      const [s, b, sm, p, u] = results;
      if (s.status === "fulfilled") {
        const { timeline: tl, ml_metrics: ml, ...live } = s.value.data;
        setTimeline(tl || []);
        if (ml) setMlMetrics(ml);
        setStats((prev) => ({ ...(prev || {}), ...live }));
      }
      if (b.status === "fulfilled") setBlockedIPs(new Set(b.value.data.blocked || []));
      if (sm.status === "fulfilled") setSim({ paused: !!sm.value.data.paused, simulated: !!sm.value.data.simulated });
      if (p.status === "fulfilled") {
        setPredictions(p.value.data.predictions || []);
        setScore(p.value.data.threat_score || null);
      }
      if (u.status === "fulfilled") setBlockedUsers(new Set((u.value.data.blocked || []).map((x) => String(x).toLowerCase())));
      setApiDown(s.status === "rejected");
    };
    poll();
    const id = setInterval(poll, 10000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  // Re-evaluate the 10-minute window periodically even without new events
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);
  // eslint-disable-next-line
  const level = useMemo(() => assessLevel(threats, alerts, Date.now()), [threats, alerts, tick]);

  // Actions
  const blockIP = useCallback(
    async (ip) => {
      try {
        await api.post("/api/ips/block", null, { params: { ip } });
        setBlockedIPs((p) => new Set([...p, ip]));
        toast(`${ip} blocked`, "ok", { detail: "Further traffic from this source is dropped until you release it." });
        return true;
      } catch (e) {
        toast("Couldn't block this source", "critical", { detail: e?.response?.data?.detail || e.message });
        return false;
      }
    },
    [toast],
  );
  const allowIP = useCallback(
    async (ip) => {
      try {
        await api.post("/api/ips/allow", null, { params: { ip } });
        setBlockedIPs((p) => {
          const n = new Set(p);
          n.delete(ip);
          return n;
        });
        toast(`${ip} released`, "ok", { detail: "The source can reach the platform again." });
        return true;
      } catch (e) {
        toast("Couldn't release this source", "critical", { detail: e?.response?.data?.detail || e.message });
        return false;
      }
    },
    [toast],
  );
  const blockUser = useCallback(
    async (username) => {
      try {
        await api.post("/api/users/block", null, { params: { username } });
        setBlockedUsers((p) => new Set([...p, username.toLowerCase()]));
        toast(`@${username} can no longer sign in`, "ok");
        return true;
      } catch (e) {
        toast("Couldn't block this account", "critical", { detail: e?.response?.data?.detail || e.message });
        return false;
      }
    },
    [toast],
  );
  const allowUser = useCallback(
    async (username) => {
      try {
        await api.post("/api/users/allow", null, { params: { username } });
        setBlockedUsers((p) => {
          const n = new Set(p);
          n.delete(username.toLowerCase());
          return n;
        });
        toast(`@${username} can sign in again`, "ok");
        return true;
      } catch (e) {
        toast("Couldn't restore this account", "critical", { detail: e?.response?.data?.detail || e.message });
        return false;
      }
    },
    [toast],
  );
  const toggleSim = useCallback(async () => {
    if (!sim) return;
    try {
      await api.post(sim.paused ? "/api/simulation/resume" : "/api/simulation/pause");
      setSim((s) => ({ paused: !s.paused, simulated: s.paused }));
      toast(sim.paused ? "Simulated traffic resumed" : "Simulated traffic paused", "brand", {
        detail: sim.paused ? "Demo events will flow again." : "Real attacks against the demo targets are still detected.",
      });
    } catch {
      toast("Couldn't change the simulation", "critical");
    }
  }, [sim, toast]);

  const go = useCallback(
    (p, tab) => {
      setNavOpen(false);
      navigate(p, tab);
    },
    [navigate],
  );
  // Switching mode lands on that mode's home page.
  const setMode = useCallback(
    (m) => {
      setModeState(m);
      go(m === "basic" ? "home" : "overview");
    },
    [setModeState, go],
  );
  const openProfile = useCallback((ip) => {
    setDetection(null);
    setProfileIP(ip);
  }, []);
  const hunt = useCallback(
    (params) => {
      setHuntPrefill({ ...params, _at: Date.now() });
      setProfileIP(null);
      setDetection(null);
      go("investigations", "hunt");
    },
    [go],
  );

  // Page title + scroll reset
  useEffect(() => {
    document.title = `${PAGES[page].label} · CyberShield`;
    window.scrollTo(0, 0);
    setNavOpen(false);
  }, [page]);

  // Keyboard: Ctrl/Cmd+K opens the palette; Escape closes the mobile menu
  useEffect(() => {
    const on = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPalette((v) => !v);
      } else if (e.key === "Escape") {
        setNavOpen(false);
      }
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);

  const commands = useMemo(() => {
    const list = [];
    list.push({ group: "View", label: mode === "basic" ? "Switch to Advanced mode" : "Switch to Basic mode", icon: "layers", run: () => setMode(mode === "basic" ? "advanced" : "basic") });
    navGroups.forEach((g) =>
      g.items.forEach((p) => {
        list.push({ group: "Go to", label: p.label, icon: p.icon, run: () => go(p.id) });
        (TABS[p.id] || []).forEach(([id, label]) =>
          list.push({ group: "Go to", label: `${p.label} › ${label}`, icon: p.icon, run: () => go(p.id, id), keywords: id }),
        );
      }),
    );
    list.push(
      { group: "Actions", label: "Open war-room map", icon: "maximize", run: () => setWarRoom(true), keywords: "map globe fullscreen" },
      {
        group: "Actions",
        label: theme === "dark" ? "Switch to light theme" : "Switch to dark theme",
        icon: theme === "dark" ? "sun" : "moon",
        run: () => setTheme(theme === "dark" ? "light" : "dark"),
        keywords: "theme appearance dark light",
      },
      sim && {
        group: "Actions",
        label: sim.paused ? "Resume simulated traffic" : "Pause simulated traffic",
        icon: sim.paused ? "play" : "pause",
        run: toggleSim,
        keywords: "simulation demo",
      },
      { group: "Actions", label: "Generate a report", icon: "download", run: () => go("reports"), keywords: "pdf docx export" },
      { group: "Actions", label: "Sign out", icon: "logout", run: onLogout },
      ...DEMO_LINKS.map((l) => ({
        group: "Live demo",
        label: `Open ${l.label.toLowerCase()}`,
        icon: l.icon,
        run: () => window.open(l.href, "_blank", "noopener"),
        keywords: "aurora bank target honeypot admin trap demo",
      })),
    );
    return list.filter(Boolean);
  }, [go, theme, setTheme, sim, toggleSim, onLogout, mode, setMode, navGroups]);

  const ipActions = useCallback(
    (q) => {
      if (isIPv4(q))
        return [
          { group: "Source", label: `Open profile for ${q}`, icon: "user", run: () => openProfile(q) },
          { group: "Source", label: `Hunt events from ${q}`, icon: "crosshair", run: () => hunt({ source_ip: q }) },
        ];
      // Partial address: suggest matching sources from the live window
      if (!/^[\d.]{3,}$/.test(q)) return [];
      const seen = new Set();
      const out = [];
      for (const t of threats) {
        const ip = t.source_ip;
        if (!ip || seen.has(ip) || !ip.includes(q)) continue;
        seen.add(ip);
        out.push({ group: "Live sources", label: `Open profile for ${ip}${t.country ? ` (${t.country})` : ""}`, icon: "user", run: () => openProfile(ip) });
        if (out.length >= 6) break;
      }
      return out;
    },
    [openProfile, hunt, threats],
  );

  const ctx = {
    mode,
    setMode,
    portal,
    threats,
    alerts,
    stats,
    timeline,
    mlMetrics,
    blockedIPs,
    blockedUsers,
    sim,
    score,
    predictions,
    level,
    connected,
    route,
    tab: route.tab,
    navigate: go,
    openProfile,
    openDetection: setDetection,
    openWarRoom: () => setWarRoom(true),
    hunt,
    huntPrefill,
    clearHuntPrefill: () => setHuntPrefill(null),
    blockIP,
    allowIP,
    blockUser,
    allowUser,
    theme,
    setTheme,
    rail,
    setRail,
    onLogout,
  };

  const counts = {
    detections: { value: level.open, tone: level.openCritical ? "critical" : "medium" },
    response: { value: blockedIPs.size, tone: "neutral" },
  };

  const showDisconnected = !connected && (everConnected || graceOver);
  const mainRef = useRef(null);

  const PageComp = {
    home: Basic,
    overview: Overview,
    detections: Detections,
    investigations: Investigations,
    response: Response,
    analytics: Analytics,
    models: Models,
    risk: Risk,
    reports: Reports,
    assistant: Assistant,
    settings: Settings,
  }[page];

  return (
    <ConsoleContext.Provider value={ctx}>
      <a className="skip-link" href="#main-content" onClick={(e) => { e.preventDefault(); mainRef.current?.focus(); }}>
        Skip to content
      </a>
      <div className={cx("app", rail && "is-rail", navOpen && "is-nav-open")}>
        <Sidebar
          groups={navGroups}
          links={DEMO_LINKS}
          page={page}
          onNavigate={(p) => go(p)}
          counts={counts}
          connected={connected}
          simulated={sim ? !sim.paused : true}
          rail={rail}
          onToggleRail={() => setRail((v) => !v)}
        />
        <div className="scrim" onClick={() => setNavOpen(false)} aria-hidden="true" />
        <div className="main">
          <TopBar
            pageLabel={PAGES[page].label}
            mode={mode}
            onMode={setMode}
            onAlerts={() => go("detections", "alerts")}
            alertCount={level.open}
            onMenu={() => setNavOpen(true)}
            onSearch={() => setPalette(true)}
            connected={connected}
            sim={sim}
            onToggleSim={toggleSim}
            theme={theme}
            onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
            onSettings={() => go("settings")}
            onLogout={onLogout}
          />
          {showDisconnected && (
            <Banner
              tone={apiDown ? "critical" : "medium"}
              icon={apiDown ? "alertCircle" : "wifi"}
              title={apiDown ? "Backend unreachable." : "Live stream interrupted."}
              action={
                <Button size="sm" variant="ghost" icon="refresh" onClick={() => window.location.reload()}>
                  Reload
                </Button>
              }
            >
              {apiDown
                ? "The API is not responding. Figures below are the last values received and may be out of date."
                : "Reconnecting automatically. New detections will appear once the connection is back."}
            </Banner>
          )}
          <main id="main-content" ref={mainRef} tabIndex={-1} style={{ outline: "none" }}>
            <div className="page" key={page}>
              <PageComp />
            </div>
          </main>
        </div>
      </div>

      <DetectionDrawer threat={detection} onClose={() => setDetection(null)} />
      <AttackerProfile ip={profileIP} onClose={() => setProfileIP(null)} />
      <WarRoom open={warRoom} onClose={() => setWarRoom(false)} threats={threats} connected={connected} onSelect={(ip) => { setWarRoom(false); openProfile(ip); }} />
      <CommandPalette open={palette} onClose={() => setPalette(false)} commands={commands} ipActions={ipActions} />
    </ConsoleContext.Provider>
  );
}
